import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import withStoreAuth from "../../../auth/StoreRouteAuth";
import StoreLayout from "./StoreLayout";
import {Badge, Btn, Dialog, Empty, Loading, Notice, Panel, SectionHead, kstGreeting} from "./StoreUI";
import {storeGet, storePost} from "../../../services/storeApi";
import {TbKey, TbCopy, TbTrash, TbRefresh, TbEye, TbEyeOff, TbArrowUpRight} from "react-icons/tb";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 대시보드
 * ════════════════════════════════════════════════════════════════════════════════
 *  경로 : /store/api/dashboard
 *
 *  [구성]
 *   1) 상단 요약    : 총매출 / 총주문 수 / 총유입고객(중복X) / 오늘유입고객
 *                    — 숫자마다 파스텔 오브를 뒤에 깔아 2D 판 위에 3D 로 떠 보이게 한다
 *   2) 기간선택     : 사용자지정 날짜 + 오늘/최근3일/최근1주/최근1개월/최근1년
 *                    (기본값 = 한국시간 기준 1년 전 ~ 오늘)
 *   3) API 키 관리  : 발급 / 재발급 / 삭제 / 마스킹 출력 / 활성·비활성 상태
 *   4) 판매상품 목록 : 인기상품순(판매수량 내림차순)
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 요약 타일 — 숫자 뒤에 파스텔 오브가 떠 있다 */
function StatTile({label, sub, value, orb, delay}) {
    return (
        <Panel lift className={`st-rise ${delay} tw-relative tw-overflow-hidden tw-p-5`}>
            <span aria-hidden="true"
                  className="tw-pointer-events-none tw-absolute tw--right-6 tw--top-8 tw-h-[104px] tw-w-[104px] tw-rounded-full tw-animate-breathe"
                  style={{background: orb, filter: 'blur(2px)'}}/>
            <p className="tw-relative tw-text-[11.5px] tw-font-semibold tw-text-[color:var(--st-text-faint)]">
                {label}
                {sub ? <span className="tw-ml-1.5 tw-font-normal tw-opacity-75">({sub})</span> : null}
            </p>
            <p className="st-num tw-relative tw-mt-3 tw-text-[26px] tw-font-bold tw-leading-none tw-text-[color:var(--st-text)] tw-break-all">
                {value}
            </p>
        </Panel>
    );
}

function Dashboard() {

    const navigate = useNavigate();

    const [info, setInfo] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [activeRange, setActiveRange] = useState('1y');

    // API 키
    const [apiKeyVisible, setApiKeyVisible] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');   // 방금 발급받은 평문키
    const [keyWorking, setKeyWorking] = useState(false);
    const [copyMsg, setCopyMsg] = useState('');
    const [confirmDeleteKey, setConfirmDeleteKey] = useState(false);

    // ── 날짜 유틸 (한국시간 기준) ────────────────────────────────────────────
    const kstNow = () => new Date(new Date().toLocaleString('en-US', {timeZone: 'Asia/Seoul'}));
    const toYmd = (d) => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const toInputDate = (ymd) => ymd ? `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}` : '';
    const fromInputDate = (v) => v ? v.replace(/-/g, '') : '';

    const RANGES = [
        {key: 'today', label: '오늘', days: 0},
        {key: '3d', label: '최근3일', days: 2},
        {key: '1w', label: '최근1주일', days: 6},
        {key: '1m', label: '최근1개월', months: 1},
        {key: '1y', label: '최근1년', years: 1},
    ];

    const calcRange = (range) => {
        const end = kstNow();
        const start = kstNow();
        if (range.years) start.setFullYear(start.getFullYear() - range.years);
        else if (range.months) start.setMonth(start.getMonth() - range.months);
        else start.setDate(start.getDate() - (range.days || 0));
        return {start: toYmd(start), end: toYmd(end)};
    };

    // ── 조회 ───────────────────────────────────────────────────────────────
    const fetchDashboard = useCallback(async (s, e) => {
        setLoading(true);
        try {
            const res = await storeGet('/store/dashboard', {startDate: s || undefined, endDate: e || undefined});
            setData(res.data);
            setStartDate(res.data?.startDate || '');
            setEndDate(res.data?.endDate || '');
        } catch (err) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        storeGet('/store/myInfo').then(res => setInfo(res.data)).catch(() => setInfo(null));
        fetchDashboard(); // 기본값(1년 전 ~ 오늘)
    }, [fetchDashboard]);

    const applyRange = (range) => {
        setActiveRange(range.key);
        const {start, end} = calcRange(range);
        fetchDashboard(start, end);
    };

    const applyCustom = () => {
        setActiveRange('custom');
        fetchDashboard(startDate, endDate);
    };

    // ── API 키 ─────────────────────────────────────────────────────────────
    const issueApiKey = async () => {
        if (keyWorking) return;
        setKeyWorking(true);
        try {
            const res = await storePost('/store/issueApiKey');
            setNewApiKey(res?.data?.apiKey || '');
            setApiKeyVisible(true);
            const me = await storeGet('/store/myInfo');
            setInfo(me.data);
        } catch (e) {
            /* 실패 시 상태 유지 */
        } finally {
            setKeyWorking(false);
        }
    };

    const deleteApiKey = async () => {
        if (keyWorking) return;
        setKeyWorking(true);
        try {
            await storePost('/store/deleteApiKey');
            setNewApiKey('');
            setApiKeyVisible(false);
            setConfirmDeleteKey(false);
            const me = await storeGet('/store/myInfo');
            setInfo(me.data);
        } catch (e) {
            /* 무시 */
        } finally {
            setKeyWorking(false);
        }
    };

    const currentApiKey = newApiKey || info?.apiKey || '';

    const maskKey = (key) => {
        if (!key) return '';
        if (key.length <= 12) return '••••••••';
        return `${key.slice(0, 8)}${'•'.repeat(18)}${key.slice(-4)}`;
    };

    const copyApiKey = async () => {
        if (!currentApiKey) return;
        try {
            await navigator.clipboard.writeText(currentApiKey);
            setCopyMsg('복사했습니다');
        } catch (e) {
            setCopyMsg('복사하지 못했습니다. 키를 직접 선택해 복사해주세요');
        }
        setTimeout(() => setCopyMsg(''), 1800);
    };

    // ── 표시 유틸 ──────────────────────────────────────────────────────────
    const won = (v) => (v == null ? '0' : Number(v).toLocaleString());

    const activated = info?.activateFl === true;

    const SUMMARY = [
        {
            label: '총매출', value: `₩ ${won(data?.totalSales)}`, delay: 'st-rise-1',
            orb: 'radial-gradient(circle at 40% 40%, rgba(255,190,72,.55), transparent 68%)',
        },
        {
            label: '총주문 수', value: `${won(data?.totalOrderCount)} 건`, delay: 'st-rise-2',
            orb: 'radial-gradient(circle at 40% 40%, rgba(203,182,255,.55), transparent 68%)',
        },
        {
            label: '총유입고객', sub: '중복 제외', value: `${won(data?.totalInflowCount)} 명`, delay: 'st-rise-3',
            orb: 'radial-gradient(circle at 40% 40%, rgba(168,221,228,.6), transparent 68%)',
        },
        {
            label: '오늘유입고객', sub: '하루 1회 기준', value: `${won(data?.todayInflowCount)} 명`, delay: 'st-rise-4',
            orb: 'radial-gradient(circle at 40% 40%, rgba(159,216,180,.55), transparent 68%)',
        },
    ];

    return (
        <StoreLayout active="dashboard"
                     eyebrow="Overview"
                     title={info?.companyName ? `${info.companyName} 님, ${kstGreeting()}` : '대시보드'}
                     /* 상호가 길어져도 두 줄로 자연스럽게 흐르도록 제목을 작게 잡는다 */
                     titleClassName="tw-text-[14px] sm:tw-text-[17px]"
                     desc="항상 귀사와 담당자님의 번창과 안녕을 기원합니다.">

            {/*═══ 요약 ═══*/}
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
                {SUMMARY.map(card => <StatTile key={card.label} {...card}/>)}
            </div>

            {/*═══ 기간선택 ═══*/}
            <Panel className="st-rise st-rise-5 tw-mt-4 tw-p-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    {/* 기간 프리셋 — 세그먼트 컨트롤 */}
                    <div role="tablist" aria-label="조회기간"
                         className="st-tabs">
                        {RANGES.map(r => (
                            <button key={r.key} type="button" role="tab"
                                    aria-selected={activeRange === r.key}
                                    onClick={() => applyRange(r)}
                                    className="st-tab">
                                {r.label}
                            </button>
                        ))}
                    </div>

                    <div className="tw-hidden tw-flex-1 lg:tw-block"/>

                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <input type="date" className="st-input st-num tw-w-auto tw-py-2 tw-text-[12.5px]"
                               aria-label="조회 시작일"
                               value={toInputDate(startDate)}
                               onChange={(e) => setStartDate(fromInputDate(e.target.value))}/>
                        <span aria-hidden="true" className="tw-text-[color:var(--st-text-faint)]">~</span>
                        <input type="date" className="st-input st-num tw-w-auto tw-py-2 tw-text-[12.5px]"
                               aria-label="조회 종료일"
                               value={toInputDate(endDate)}
                               onChange={(e) => setEndDate(fromInputDate(e.target.value))}/>
                        <Btn size="md" tone={activeRange === 'custom' ? 'dawn' : 'lilac'} onClick={applyCustom}>조회</Btn>
                    </div>
                </div>
            </Panel>

            {/*═══ API 키 ═══*/}
            <Panel className="st-rise st-rise-5 tw-mt-4 tw-p-6">
                <SectionHead
                    eyebrow="Integration"
                    title="API 키"
                    desc="쇼핑몰에 삽입한 비토리지 버튼이 이 키로 인증됩니다"
                    right={<Badge tone={activated ? 'sage' : 'blush'} dot>{activated ? '활성' : '비활성'}</Badge>}
                />

                {!activated ? (
                    <div className="tw-mb-5">
                        <Notice tone="warn">
                            지금은 <b className="tw-text-[#D0604A]">비활성</b> 상태입니다. 쇼핑몰 고객이 비토리지 버튼을 눌러도 연동이 거부됩니다.
                            비토리지가 확인 후 활성화하면 바로 사용하실 수 있습니다.
                        </Notice>
                    </div>
                ) : null}

                {currentApiKey ? (
                    <>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-[16px] tw-border tw-px-4 tw-py-3"
                             style={{background: 'var(--st-field)', borderColor: 'var(--st-glass-line)'}}>
                            <code className="st-mono tw-min-w-[200px] tw-flex-1 tw-break-all tw-text-[12.5px] tw-text-[color:var(--st-text)]">
                                {apiKeyVisible ? currentApiKey : maskKey(currentApiKey)}
                            </code>
                            <Btn size="sm" tone="glass" onClick={() => setApiKeyVisible(v => !v)}
                                 aria-label={apiKeyVisible ? 'API 키 가리기' : 'API 키 보기'}>
                                {apiKeyVisible ? <TbEyeOff/> : <TbEye/>}
                            </Btn>
                            <Btn size="sm" tone="glass" onClick={copyApiKey}><TbCopy/> 복사</Btn>
                        </div>

                        {copyMsg ? (
                            <p className="tw-mt-2 tw-px-1 tw-text-[12px] tw-text-[#B8790F]">{copyMsg}</p>
                        ) : null}

                        {newApiKey ? (
                            <div className="tw-mt-3">
                                <Notice tone="warn">
                                    방금 발급된 키입니다. 지금 안전한 곳에 보관해주세요. 재발급하면 이전 키는 즉시 무효가 됩니다.
                                </Notice>
                            </div>
                        ) : null}

                        <div className="tw-mt-5 tw-flex tw-flex-wrap tw-gap-3">
                            <Btn size="md" tone="lilac" onClick={issueApiKey} disabled={keyWorking}>
                                <TbRefresh/> 재발급
                            </Btn>
                            <Btn size="md" tone="blush" onClick={() => setConfirmDeleteKey(true)} disabled={keyWorking}>
                                <TbTrash/> 삭제
                            </Btn>
                        </div>
                    </>
                ) : (
                    <div className="tw-py-6 tw-text-center">
                        <div aria-hidden="true"
                             className="tw-relative tw-mx-auto tw-mb-5 tw-flex tw-h-[72px] tw-w-[72px] tw-items-center tw-justify-center">
                            <span className="tw-absolute tw-inset-0 tw-rounded-full tw-animate-breathe"
                                  style={{background: 'radial-gradient(circle at 36% 32%, rgba(255,190,72,.5), rgba(203,182,255,.3) 60%, transparent 72%)'}}/>
                            <TbKey className="tw-relative tw-text-[30px] tw-text-[#B8790F]"/>
                        </div>
                        <p className="st-display tw-text-[15.5px] tw-text-[color:var(--st-text)]">아직 API 키가 없습니다</p>
                        <p className="tw-mx-auto tw-mt-2 tw-max-w-[360px] tw-text-[12.5px] tw-leading-[1.8] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                            키를 발급하고 쇼핑몰에 스크립트 한 줄만 붙이면 연동이 끝납니다.
                        </p>
                        <Btn tone="dawn" size="lg" className="tw-mt-6" onClick={issueApiKey}
                             disabled={keyWorking} busy={keyWorking}>
                            API 키 발급
                        </Btn>
                    </div>
                )}

                <div className="tw-mt-5 tw-border-t tw-pt-4" style={{borderColor: 'var(--st-glass-line)'}}>
                    <button type="button" onClick={() => navigate('/store/api/docs')}
                            className="st-link tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[12.5px]">
                        API 명세서 · 연동가이드 · 버튼 가이드라인 보기 <TbArrowUpRight/>
                    </button>
                </div>
            </Panel>

            {/*═══ 판매상품 ═══*/}
            <Panel className="st-rise st-rise-5 tw-mt-4 tw-p-6">
                <SectionHead eyebrow="Products" title="판매상품" desc="선택한 기간에 연동된 상품을 판매수량이 많은 순으로 보여드립니다"/>

                {loading ? <Loading label="판매내역을 불러오는 중"/>
                    : (!data?.productList || data.productList.length === 0) ? (
                        <Empty title="이 기간에는 연동된 판매가 없습니다">
                            기간을 넓혀보시거나, 쇼핑몰에 비토리지 버튼이 잘 붙어 있는지 확인해보세요.
                        </Empty>
                    ) : (
                        <div className="st-scroll tw-overflow-x-auto">
                            <table className="tw-w-full tw-min-w-[700px]">
                                <thead>
                                <tr>
                                    {[
                                        {h: '순위', align: 'tw-text-left'},
                                        {h: '상품명', align: 'tw-text-left'},
                                        {h: '옵션', align: 'tw-text-left'},
                                        {h: '판매수량', align: 'tw-text-right'},
                                        {h: '판매금액', align: 'tw-text-right'},
                                        {h: 'URL', align: 'tw-text-left'},
                                    ].map(c => (
                                        <th key={c.h}
                                            className={`tw-whitespace-nowrap tw-border-b tw-px-3 tw-py-2.5 tw-text-[11.5px] tw-font-semibold tw-text-[color:var(--st-text-faint)] ${c.align}`}
                                            style={{borderColor: 'var(--st-glass-line)'}}>{c.h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {data.productList.map((p, idx) => (
                                    <tr key={`${p.name}-${p.option}-${idx}`}
                                        className="tw-transition-colors hover:tw-bg-[rgba(255,190,72,.07)]">
                                        <td className="tw-border-b tw-px-3 tw-py-3" style={{borderColor: 'var(--st-glass-line)'}}>
                                            <span className="st-num tw-inline-flex tw-h-[26px] tw-w-[26px] tw-items-center tw-justify-center tw-rounded-full tw-text-[12px] tw-font-bold"
                                                  style={idx < 3
                                                      ? {background: 'linear-gradient(135deg,#FFD79A,#FFBE48)', color: '#4A3410'}
                                                      : {background: 'rgba(203,182,255,.20)', color: 'var(--st-text-sub)'}}>
                                                {idx + 1}
                                            </span>
                                        </td>
                                        <td className="tw-max-w-[280px] tw-break-all tw-border-b tw-px-3 tw-py-3 tw-text-[13px] tw-text-[color:var(--st-text)]"
                                            style={{borderColor: 'var(--st-glass-line)'}}>{p.name}</td>
                                        <td className="tw-max-w-[180px] tw-break-all tw-border-b tw-px-3 tw-py-3 tw-text-[12.5px] tw-text-[color:var(--st-text-sub)]"
                                            style={{borderColor: 'var(--st-glass-line)'}}>{p.option || '—'}</td>
                                        <td className="st-num tw-whitespace-nowrap tw-border-b tw-px-3 tw-py-3 tw-text-right tw-text-[13px] tw-text-[color:var(--st-text)]"
                                            style={{borderColor: 'var(--st-glass-line)'}}>{won(p.totalQuantity)}</td>
                                        <td className="st-num tw-whitespace-nowrap tw-border-b tw-px-3 tw-py-3 tw-text-right tw-text-[13px] tw-font-semibold tw-text-[color:var(--st-text)]"
                                            style={{borderColor: 'var(--st-glass-line)'}}>₩ {won(p.totalAmount)}</td>
                                        <td className="tw-whitespace-nowrap tw-border-b tw-px-3 tw-py-3 tw-text-[12.5px]"
                                            style={{borderColor: 'var(--st-glass-line)'}}>
                                            {p.url ? (
                                                <a href={p.url} target="_blank" rel="noopener noreferrer"
                                                   className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[#B8790F] tw-underline tw-underline-offset-2 hover:tw-text-[#F0A31F]">
                                                    바로가기 <TbArrowUpRight/>
                                                </a>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </Panel>

            {/*API 키 삭제 확인*/}
            {confirmDeleteKey ? (
                <Dialog title="API 키를 삭제할까요?"
                        confirmLabel="삭제" tone="blush" busy={keyWorking}
                        onCancel={() => setConfirmDeleteKey(false)}
                        onConfirm={deleteApiKey}>
                    삭제하면 쇼핑몰에 삽입된 비토리지 버튼이 즉시 동작을 멈춥니다.
                    다시 쓰시려면 키를 새로 발급하고 스크립트의 키 값을 바꿔주셔야 합니다.
                </Dialog>
            ) : null}
        </StoreLayout>
    );
}

export default withStoreAuth(Dashboard);
